import React from 'react';
import {useWindowDimensions, View} from 'react-native';

import Icon from '/component/base/Icon/Icon';
import Text from '/component/base/Text';
import {DashboardID} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import testProperties from '/util/testing/testProperties';

import {styles} from './TextOnlyBanner.style';
import {TextOnlyBannerProps} from './TextOnlyBanner.types';
import {generateOnLearnMore} from './TextOnlyBanner.util';

export const TextOnlyBanner = ({
  popoverShow = () => {},
  parentScreenName: currentScreen,
  cardNumber = null,
  totalCards = null,
  bannerHeight = null,
}: TextOnlyBannerProps) => {
  const {fontScale} = useWindowDimensions();
  const isLargeFont = fontScale > 1.15;
  const {tt, ts} = useTranslation('TextOnlyBanner');
  const renderDescription = () => {
    return tt('content', () => (
      <Text
        allowFontScaling
        maxFontSizeMultiplier={2}
        {...testProperties(
          DashboardID.dashboardIncentiveTripBannerDescription,
        )}>
        Qualify for the trip by meeting the following requirements. Your
        requirements (or path) are determined by your performing rank in April
        2023.{' '}
        {tt('learnMore', () => (
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            onPress={generateOnLearnMore({popoverShow, currentScreen})}
            style={styles.link}
            {...testProperties(
              DashboardID.dashboardIncentiveTripBannerLearnMoreLink,
            )}>
            Learn more
          </Text>
        ))}
      </Text>
    ));
  };

  return (
    <View
      style={{
        ...styles.incentiveTripContent,
        ...(bannerHeight ? {minHeight: bannerHeight} : {}),
      }}
      {...testProperties(DashboardID.dashboardIncentiveTripBanner)}>
      <View
        style={[
          styles.headerContainer,
          isLargeFont && styles.headerContainerLargeFont,
        ]}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          style={styles.headerText}
          {...testProperties(DashboardID.dashboardIncentiveTripBannerTitle)}>
          {tt('heading', () => (
            <>
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                variant="heading"
                style={styles.optaText}>
                OPTA
              </Text>
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                variant="heading"
                style={styles.titleText}>
                VIA Incentive Trip 2024
              </Text>
            </>
          ))}
        </Text>
        {totalCards &&
          cardNumber &&
          tt('sideTitle', () => (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              variant="button"
              style={[styles.sideText, isLargeFont && styles.sideTextLargeFont]}
              {...testProperties(
                DashboardID.dashboardIncentiveTripBannerSideTitle,
              )}>
              {cardNumber} of {totalCards}
            </Text>
          ))}
      </View>
      {renderDescription()}
      <View
        style={styles.divider}
        {...testProperties(DashboardID.dashboardIncentiveTripBannerDivider)}
      />
      <View
        style={styles.dataContainer}
        {...testProperties(
          DashboardID.dashboardIncentiveTripBannerDataContainer,
        )}>
        <View style={styles.subHeading}>
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            variant="caption"
            style={styles.subHeadingText}
            {...testProperties(
              DashboardID.dashboardIncentiveTripBannerCaption,
            )}>
            {ts('ReportComingSoon', 'Individual reporting coming soon')}
          </Text>
        </View>
        <View
          style={styles.progressBarContainer}
          {...testProperties(
            DashboardID.dashboardIncentiveTripBannerProgressBarContainer,
          )}>
          <Icon
            name="circle-checkmark"
            style={{...styles.circleIcon}}
            {...testProperties(
              DashboardID.dashboardIncentiveTripBannerProgressCompleteIcon,
            )}
          />
          <View
            style={styles.emptyBox}
            {...testProperties(
              DashboardID.dashboardIncentiveTripBannerProgressPendingBox,
            )}
          />
        </View>
        <View
          style={styles.boxesContainer}
          {...testProperties(
            DashboardID.dashboardIncentiveTripBannerProgressBoxesContainer,
          )}
        />
      </View>
    </View>
  );
};
